import { useQuery } from '@tanstack/react-query';
import { get, type ApiError } from '../../lib/api';
import { calendarDate } from '../../lib/format';
import { Field, Input, Textarea } from '../../components/ui';
import type { FormPriorityMatrix } from './priority-matrix';

/**
 * The fields an administrator added to the ticket form.
 *
 * These existed on both sides and met in the middle nowhere. An admin could
 * create "Serial number", mark it required of agents, and it appeared on no
 * form in the product — only in the ticket list's filter panel, where somebody
 * could filter on a value nothing was able to set. The catalogue had the
 * mirror-image problem: a requester's answers were stored on the ticket and
 * displayed on no screen, so the agent picking the ticket up could not find out
 * which laptop had been asked for.
 *
 * The definitions come from the server, which decides what a requester may see
 * and what is required of whom, so a field hidden from requesters is genuinely
 * absent rather than merely undrawn.
 */

export interface TicketFormField {
  key: string;
  label: string;
  description: string | null;
  placeholder: string | null;
  field_type: string;
  required: boolean;
  required_to_close: boolean;
  editable: boolean;
  choices: Array<{ value: string; label: string }>;
  default_value: unknown;
  validation: Record<string, unknown>;
}

/** Types whose value is chosen rather than typed. */
const CHOICE_TYPES = new Set(['dropdown', 'radio', 'multi_select']);

/** What the browser should validate before anything is sent. */
const INPUT_TYPES: Record<string, string> = {
  number: 'number',
  decimal: 'number',
  date: 'date',
  datetime: 'datetime-local',
  email: 'email',
  phone: 'tel',
  url: 'url',
};

/**
 * The rules a ticket form follows besides its fields, as the server applies
 * them — so a form asks for what the server will ask for, and does not offer a
 * requester a priority the server would then refuse.
 */
export interface TicketFormMeta {
  allow_requester_priority: boolean;
  require_resolution_note: boolean;
  reopen_window_days: number;
  default_priority: string | null;
  /**
   * The priority each urgency and impact gives, for agents; null for a
   * requester, who never sets either. Absent from older servers.
   */
  priority_matrix?: FormPriorityMatrix | null;
}

/**
 * The fields a ticket form should ask for.
 *
 * Pass a ticket number on a screen showing an existing ticket, and the reply
 * also carries the catalogue item's own fields — a request raised from the
 * catalogue stores its answers under the item's keys, not the ticket's, so
 * without them the agent's screen has a label for none of them.
 */
export function useTicketFormFields(ticketNumber?: number) {
  return useQuery<{ data: TicketFormField[]; meta?: TicketFormMeta }, ApiError>({
    queryKey: ['ticket-form-fields', ticketNumber ?? null],
    queryFn: () =>
      get(ticketNumber ? `/tickets/form-fields?ticket=${ticketNumber}` : '/tickets/form-fields'),
    // Rarely changes, and read by every ticket form on the screen.
    staleTime: 5 * 60_000,
  });
}

/**
 * The values a form should start with.
 *
 * `default_value` was stored, shipped to the browser and applied by nothing, so
 * a default was a note to nobody. Existing values win over it: a default is
 * what to use in the absence of an answer, not instead of one.
 */
export function seedCustomFields(
  fields: TicketFormField[],
  existing: Record<string, unknown> = {},
): Record<string, unknown> {
  const seeded: Record<string, unknown> = {};
  for (const field of fields) {
    const current = existing[field.key];
    if (current !== undefined && current !== null && current !== '') {
      seeded[field.key] = current;
    } else if (field.default_value !== null && field.default_value !== undefined) {
      seeded[field.key] = field.default_value;
    }
  }
  return seeded;
}

/** Which required fields have not been answered, by label. */
export function unansweredRequired(
  fields: TicketFormField[],
  values: Record<string, unknown>,
): string[] {
  return fields
    .filter((field) => {
      if (!field.required) return false;
      const value = values[field.key];
      return (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      );
    })
    .map((field) => field.label);
}

export function CustomFieldInputs({
  fields,
  values,
  onChange,
  errors = {},
  disabled,
  idPrefix = 'cf',
  requiredKeys,
}: {
  fields: TicketFormField[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  /** Keeps ids unique when the same field is drawn twice on one screen. */
  idPrefix?: string;
  /** Fields to mark as required here, whatever the definition says — e.g. those needed to resolve. */
  requiredKeys?: ReadonlySet<string>;
}) {
  if (fields.length === 0) return null;

  return (
    <>
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`;
        const value = values[field.key];
        const readOnly = disabled || !field.editable;
        const error = errors[`custom_fields.${field.key}`] ?? errors[field.key];
        // The error is announced from the control itself, not only drawn
        // under it: `Field` renders it with the id `${id}-error`.
        const described = {
          'aria-invalid': error ? true : undefined,
          'aria-describedby': error ? `${id}-error` : undefined,
        } as const;

        return (
          <Field
            key={field.key}
            label={field.label}
            htmlFor={id}
            required={field.required || requiredKeys?.has(field.key)}
            error={error}
            hint={
              field.description ??
              (field.required_to_close && !field.required
                ? 'Needed before the ticket can be resolved.'
                : undefined)
            }
          >
            {CHOICE_TYPES.has(field.field_type) ? (
              <select
                id={id}
                {...described}
                multiple={field.field_type === 'multi_select'}
                disabled={readOnly}
                value={
                  field.field_type === 'multi_select'
                    ? ((value as string[]) ?? []).map(String)
                    : value === undefined || value === null
                      ? ''
                      : String(value)
                }
                onChange={(event) =>
                  onChange(
                    field.key,
                    field.field_type === 'multi_select'
                      ? [...event.target.selectedOptions].map((option) => option.value)
                      : event.target.value || null,
                  )
                }
                className="w-full rounded-control border border-line-strong bg-surface px-2 py-1 text-body text-ink disabled:opacity-60 aria-[invalid=true]:border-critical"
              >
                {/* A single choice needs a way back to "not answered"; a
                    multi-select expresses that by having nothing selected. */}
                {field.field_type !== 'multi_select' && <option value="">—</option>}
                {field.choices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            ) : field.field_type === 'checkbox' ? (
              <label className="flex items-center gap-2 text-body text-ink">
                <input
                  id={id}
                  {...described}
                  type="checkbox"
                  disabled={readOnly}
                  checked={value === true}
                  onChange={(event) => onChange(field.key, event.target.checked)}
                  className="size-3.5 rounded border-line-strong text-accent"
                />
                {field.placeholder ?? 'Yes'}
              </label>
            ) : field.field_type === 'paragraph' ? (
              <Textarea
                id={id}
                {...described}
                className="min-h-24"
                disabled={readOnly}
                value={value === undefined || value === null ? '' : String(value)}
                placeholder={field.placeholder ?? undefined}
                onChange={(event) => onChange(field.key, event.target.value || null)}
              />
            ) : (
              <Input
                id={id}
                {...described}
                type={INPUT_TYPES[field.field_type] ?? 'text'}
                disabled={readOnly}
                value={value === undefined || value === null ? '' : String(value)}
                placeholder={field.placeholder ?? undefined}
                onChange={(event) => onChange(field.key, event.target.value || null)}
              />
            )}
          </Field>
        );
      })}
    </>
  );
}

/**
 * What a ticket's custom fields say, for reading rather than editing.
 *
 * Used on the ticket screen, where the answers to a catalogue form finally
 * become visible to the agent who has to act on them.
 */
export function CustomFieldSummary({
  fields,
  values,
}: {
  fields: TicketFormField[];
  values: Record<string, unknown>;
}) {
  const answered = fields
    .map((field) => ({ field, text: displayValue(field, values[field.key]) }))
    .filter((entry) => entry.text !== null);

  if (answered.length === 0) return null;

  return (
    <dl className="space-y-1.5">
      {answered.map(({ field, text }) => (
        <div key={field.key} className="grid grid-cols-[7.5rem_1fr] gap-2">
          <dt dir="auto" className="text-mini text-ink-subtle">
            {field.label}
          </dt>
          <dd dir="auto" className="min-w-0 text-body break-words whitespace-pre-wrap text-ink">
            {text}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** "Not answered", whichever way it was stored. */
export function isUnanswered(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/** A stored value as a person would read it, or null when there is nothing. */
export function displayValue(field: TicketFormField, value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.map((one) => labelFor(field, one)).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && (field.field_type === 'date' || field.field_type === 'datetime')) {
    return calendarDate(value);
  }
  return labelFor(field, value);
}

/** Choices are stored by value and read by label. */
function labelFor(field: TicketFormField, value: unknown): string {
  const match = field.choices.find((choice) => String(choice.value) === String(value));
  return match ? match.label : String(value);
}
